import React from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import styles from './QRPerformanceChart.module.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

export default function QRPerformanceChart({ data }) {
    // Get top 10 QRs for display
    const topQRs = data?.slice(0, 10) || [];
    
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 0,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
            },
            tooltip: {
                backgroundColor: '#161d2d',
                titleColor: '#f6f6f6',
                bodyColor: '#f6f6f6',
                borderColor: '#161d2d',
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: false,
                callbacks: {
                    title: function(context) {
                        const qr = topQRs[context[0].dataIndex];
                        return qr ? qr.nickname : 'Unknown QR';
                    },
                    label: function(context) {
                        const qr = topQRs[context.dataIndex];
                        return [
                            `Scans: ${context.parsed.y}`,
                            `Unique Users: ${qr?.uniqueUsers || 0}`,
                            `Category: ${qr?.category || 'Uncategorized'}`
                        ];
                    }
                }
            },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
                ticks: {
                    color: '#6b7280',
                    font: {
                        size: 10,
                    },
                    maxRotation: 45,
                    minRotation: 0,
                    callback: function(value, index) {
                        const qr = topQRs[index];
                        if (qr && qr.nickname) {
                            return qr.nickname.length > 15 
                                ? qr.nickname.substring(0, 15) + '...'
                                : qr.nickname;
                        }
                        return '';
                    }
                },
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: '#f3f4f6',
                    borderDash: [2, 2],
                },
                border: {
                    display: false,
                },
                ticks: {
                    color: '#6b7280',
                    font: {
                        size: 12,
                    },
                    callback: function(value) {
                        if (value >= 1000) {
                            return (value / 1000).toFixed(1) + 'k';
                        }
                        return value;
                    },
                },
            },
        },
        elements: {
            bar: {
                borderRadius: 4,
                borderSkipped: false,
            },
        },
    };

    const chartData = {
        labels: topQRs.map((_, index) => index),
        datasets: [
            {
                label: 'Scans',
                data: topQRs.map(qr => qr.scans),
                backgroundColor: topQRs.map((_, index) => 
                    index === 0 ? '#161d2d' : `rgba(22, 29, 45, ${0.8 - (index * 0.07)})`
                ),
                borderColor: '#161d2d',
                borderWidth: 1,
            },
        ],
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h3 className={styles.title}>Top Performing QR Codes</h3>
                <div className={styles.count}>
                    {data?.length || 0} QR{(data?.length || 0) !== 1 ? 's' : ''}
                </div>
            </div>
            
            <div className={styles.chartContainer}>
                <Bar 
                    options={options} 
                    data={chartData}
                />
            </div>
            
            {/* QR List */}
            <div className={styles.qrList}>
                {topQRs.slice(0, 10).map((qr, index) => (
                    <div key={qr.id} className={styles.qrItem}>
                        <div className={styles.qrRank}>#{index + 1}</div>
                        <div className={styles.qrInfo}>
                            <div className={styles.qrName}>{qr.nickname}</div>
                            <div className={styles.qrStats}>
                                {qr.scans} scans
                            </div>
                        </div>
                        <div className={styles.qrCategory}>{qr.category}</div>
                    </div>
                ))}
            </div>
            
            {(!data || data.length === 0) && (
                <div className={styles.noData}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    <p>No QR performance data available.</p>
                </div>
            )}
        </div>
    );
} 